export type WolfRankId =
  | "cachorro"
  | "lobato"
  | "errante"
  | "rastreador"
  | "cazador"
  | "colmillo"
  | "lobo"
  | "alfa"
  | "ancestral"
  | "fenrir";

export interface WolfRank {
  id: WolfRankId;
  name: string;
  minScore: number;
  maxScore: number;
  phrase: string;
  requiresFinishedRun?: boolean;
  requiresSpecialConditions?: boolean;
}

export interface WolfRankStats {
  finishedRun: boolean;
  survivalSeconds: number;
  beatSyncPercent: number;
  collectedIsotypePercent: number;
  maxCombo: number;
  hits: number;
  missedMajorPickups: number;
}

export interface WolfRankResult {
  rank: WolfRank;
  progressText: string;
  reasonText: string;
  fenrirBlocked: boolean;
  unfinishedCapApplied: boolean;
}

export const WOLF_RANKS: WolfRank[] = [
  { id: "cachorro", name: "Cachorro", minScore: 0, maxScore: 39999, phrase: "Todavia aprendes a correr." },
  { id: "lobato", name: "Lobato", minScore: 40000, maxScore: 54999, phrase: "El bosque empezo a escucharte." },
  { id: "errante", name: "Errante", minScore: 55000, maxScore: 69999, phrase: "Sobreviviste, pero aun no dominas la ruta." },
  { id: "rastreador", name: "Rastreador", minScore: 70000, maxScore: 79999, phrase: "Ya sabes leer el bosque." },
  { id: "cazador", name: "Cazador", minScore: 80000, maxScore: 87499, phrase: "Tu instinto empieza a mandar." },
  { id: "colmillo", name: "Colmillo", minScore: 87500, maxScore: 92499, phrase: "Ya no corres como presa." },
  { id: "lobo", name: "Lobo", minScore: 92500, maxScore: 95999, phrase: "El bosque reconoce tu paso." },
  { id: "alfa", name: "Alfa", minScore: 96000, maxScore: 97999, phrase: "La ruta se abre cuando avanzas.", requiresFinishedRun: true },
  { id: "ancestral", name: "Ancestral", minScore: 98000, maxScore: 99199, phrase: "Tu nombre pesa en la raiz.", requiresFinishedRun: true },
  {
    id: "fenrir",
    name: "Fenrir",
    minScore: 99200,
    maxScore: Infinity,
    phrase: "El bosque ya no puede nombrarte.",
    requiresFinishedRun: true,
    requiresSpecialConditions: true
  }
];

export const WOLF_RANK_SCORE = {
  maxScore: 100000,
  gameDurationSeconds: 120,
  survivalMax: 25000,
  isotypeMax: 25000,
  beatSyncMax: 25000,
  flowMax: 10000,
  pressureMax: 5000,
  finishBonus: 10000,
  maxPenalty: 20000,
  maxRankIfNotFinished: "lobo" satisfies WolfRankId,
  fenrir: {
    minScore: 99200,
    survivalSeconds: 120,
    beatSyncPercent: 92,
    collectedIsotypePercent: 95,
    maxCombo: 12,
    hits: 1,
    missedMajorPickups: 1
  }
} as const;

export const getWolfRank = (score: number, stats: WolfRankStats): WolfRankResult => {
  let rank = WOLF_RANKS.find((item) => score >= item.minScore && score <= item.maxScore) ?? WOLF_RANKS[0];
  let unfinishedCapApplied = false;
  let fenrirBlocked = false;

  if (!stats.finishedRun) {
    const maxUnfinishedRank = getRankById(WOLF_RANK_SCORE.maxRankIfNotFinished);
    if (WOLF_RANKS.indexOf(rank) > WOLF_RANKS.indexOf(maxUnfinishedRank)) {
      rank = maxUnfinishedRank;
      unfinishedCapApplied = true;
    }
  }

  if (rank.id === "fenrir" && !qualifiesForFenrir(score, stats)) {
    rank = getRankById("ancestral");
    fenrirBlocked = true;
  }

  return {
    rank,
    progressText: getProgressText(score, rank, unfinishedCapApplied, fenrirBlocked),
    reasonText: getReasonText(stats, unfinishedCapApplied, fenrirBlocked),
    fenrirBlocked,
    unfinishedCapApplied
  };
};

const qualifiesForFenrir = (score: number, stats: WolfRankStats) =>
  score >= WOLF_RANK_SCORE.fenrir.minScore &&
  stats.finishedRun &&
  stats.survivalSeconds >= WOLF_RANK_SCORE.fenrir.survivalSeconds &&
  stats.beatSyncPercent >= WOLF_RANK_SCORE.fenrir.beatSyncPercent &&
  stats.collectedIsotypePercent >= WOLF_RANK_SCORE.fenrir.collectedIsotypePercent &&
  stats.maxCombo >= WOLF_RANK_SCORE.fenrir.maxCombo &&
  stats.hits <= WOLF_RANK_SCORE.fenrir.hits &&
  stats.missedMajorPickups <= WOLF_RANK_SCORE.fenrir.missedMajorPickups;

const getRankById = (id: WolfRankId) => WOLF_RANKS.find((rank) => rank.id === id) ?? WOLF_RANKS[0];

const getProgressText = (score: number, rank: WolfRank, unfinishedCapApplied: boolean, fenrirBlocked: boolean) => {
  if (unfinishedCapApplied) return "Los rangos superiores exigen llegar al final.";
  if (fenrirBlocked) return "Fenrir exige una carrera casi perfecta.";
  const nextRank = WOLF_RANKS[WOLF_RANKS.indexOf(rank) + 1];
  if (!nextRank) return "Rango maximo alcanzado.";
  const missing = Math.max(0, nextRank.minScore - score);
  return `Faltan ${missing.toLocaleString("en-US")} puntos para ${nextRank.name}.`;
};

const getReasonText = (stats: WolfRankStats, unfinishedCapApplied: boolean, fenrirBlocked: boolean) => {
  if (unfinishedCapApplied) return "Fenrir exige llegar al final.";
  if (fenrirBlocked) {
    if (stats.beatSyncPercent < WOLF_RANK_SCORE.fenrir.beatSyncPercent) return "Fenrir exige mejor sincronia.";
    if (stats.collectedIsotypePercent < WOLF_RANK_SCORE.fenrir.collectedIsotypePercent) return "Perdiste isotipos grandes.";
    if (stats.hits > WOLF_RANK_SCORE.fenrir.hits) return "Los choques rompieron tu rango.";
    if (stats.maxCombo < WOLF_RANK_SCORE.fenrir.maxCombo) return "Te falto mantener cadena.";
    if (stats.missedMajorPickups > WOLF_RANK_SCORE.fenrir.missedMajorPickups) return "Perdiste isotipos grandes.";
    return "Fenrir exige una carrera casi perfecta.";
  }
  if (stats.beatSyncPercent < 68) return "Te falto sincronia.";
  if (stats.collectedIsotypePercent < 72) return "Perdiste demasiada energia.";
  if (stats.hits >= 3) return "Los choques rompieron tu rango.";
  return "Sigue subiendo.";
};
